// Inbox kinds that other packages add (PR 2 app approvals, grants and business
// approvals). A kind registers its icon, its label and the detail it draws in
// the one canvas; the Inbox stays the shell that lists, selects and refreshes.
// The detail owns its own decision, so the canvas footer offers none for it.
import type { ComponentType } from 'react';
import type { WorkspaceIconName } from './icons';
import type { InboxItem, InboxUpdate } from './inbox-api';

/** What a kind says about one row beyond the shared meta: who did it, and its one detail. */
export interface InboxRowMeta {
  /** Replaces the actor's name: "Claude Code" for a request an agent made. */
  by?: string;
  /** Replaces the update's excerpt, or follows the kind on a Needs-you row. */
  detail?: string;
  /** Replaces the row's title (a native action named in the person's language). */
  title?: string;
}
/** What a kind says in its canvas heading; absent parts keep the Inbox's own. */
export interface InboxHeading {
  meta?: string;
  /** Replaces the item's title in the canvas heading. */
  title?: string;
  description?: string | null;
}

export interface InboxKindExtension {
  kind: string;
  icon: WorkspaceIconName;
  /** A hook (so the kind can read its own copy); called once per render, in registration order. */
  useLabel: () => string;
  Detail: ComponentType<{ item: InboxItem; onSettled: () => void }>;
  /** A hook (it may read copy and the locale); returns the row meta of an item or update of this kind. */
  useRowMeta?: () => (entry: InboxItem | InboxUpdate) => InboxRowMeta;
  /** A hook returning the canvas heading of an item or update of this kind. */
  useHeading?: () => (entry: InboxItem | InboxUpdate) => InboxHeading;
}

const extensions = new Map<string, InboxKindExtension>();
export function registerInboxKind(extension: InboxKindExtension) {
  extensions.set(extension.kind, extension);
}
export const inboxKindExtension = (kind: string) => extensions.get(kind) ?? null;

/** Labels of every registered kind, by kind; the hooks run in a stable order. */
export function useInboxKindLabels(): (kind: string) => string | null {
  const labels = new Map<string, string>();
  for (const extension of extensions.values()) labels.set(extension.kind, extension.useLabel());
  return (kind) => labels.get(kind) ?? null;
}

/** Row meta of any registered kind (empty for a kind without one); the hooks run in a stable order. */
export function useInboxRowMeta(): (entry: InboxItem | InboxUpdate) => InboxRowMeta {
  const metas = new Map<string, (entry: InboxItem | InboxUpdate) => InboxRowMeta>();
  for (const extension of extensions.values())
    if (extension.useRowMeta) metas.set(extension.kind, extension.useRowMeta());
  return (entry) => metas.get(entry.kind)?.(entry) ?? {};
}

/** Canvas heading parts of any registered kind. */
export function useInboxHeading(): (entry: InboxItem | InboxUpdate) => InboxHeading {
  const headings = new Map<string, (entry: InboxItem | InboxUpdate) => InboxHeading>();
  for (const extension of extensions.values())
    if (extension.useHeading) headings.set(extension.kind, extension.useHeading());
  return (entry) => headings.get(entry.kind)?.(entry) ?? {};
}
